import { useState, useMemo } from "react";
import CommandNav from "@/components/CommandNav";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  FileBarChart, Search, Download, Filter, Calendar, MapPin,
  User, Shield, AlertTriangle, CheckCircle2, Clock, Eye,
  ChevronRight, XCircle, ScanFace, Siren, BarChart3, Users,
  FileText, TrendingUp, Hash, Loader2, ArrowLeft, ChevronDown
} from "lucide-react";

// ─── Classification labels ───
const CLASSIFICATION_LABELS: Record<string, { label: string; color: string }> = {
  suspect_identified: { label: "Agressor Identificado", color: "text-red-400" },
  victim_identified: { label: "Vítima Identificada", color: "text-amber-400" },
  monitored_person: { label: "Pessoa Monitorada", color: "text-blue-400" },
  unidentified_person: { label: "Não Identificada", color: "text-slate-400" },
  under_analysis: { label: "Em Análise", color: "text-slate-400" },
  confirmed_incident: { label: "Incidente Confirmado", color: "text-red-400" },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  detected: { label: "Detectado", color: "text-blue-400" },
  identified: { label: "Identificado", color: "text-purple-400" },
  classified: { label: "Classificado", color: "text-indigo-400" },
  agent_dispatched: { label: "Agente Acionado", color: "text-amber-400" },
  in_progress: { label: "Em Atendimento", color: "text-cyan-400" },
  resolved: { label: "Resolvido", color: "text-green-400" },
  closed: { label: "Encerrado", color: "text-slate-400" },
};

const EVENT_TYPE_LABELS: Record<string, string> = {
  facial_recognition: "Reconhecimento Facial",
  manual_report: "Relatório Manual",
  alert: "Alerta",
  incident: "Incidente",
  patrol_check: "Verificação de Patrulha",
};

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function formatDateShort(date: Date | string | null | undefined): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

// ─── CSV Export helper ───
function downloadCSV(data: any[], filename: string) {
  if (data.length === 0) {
    toast.error("Nenhum dado para exportar");
    return;
  }
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(";"),
    ...data.map(row => headers.map(h => {
      const val = row[h];
      if (val === null || val === undefined) return "";
      if (val instanceof Date) return formatDate(val);
      const str = String(val).replace(/"/g, '""');
      return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
    }).join(";"))
  ].join("\n");

  const BOM = "\uFEFF";
  const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success("Exportação concluída");
}

// ============================================================
// TAB 1: Event Report (Individual)
// ============================================================
function EventReportTab({ initialEventId }: { initialEventId?: number | null }) {
  const [eventId, setEventId] = useState(initialEventId ? String(initialEventId) : "");
  const [searchId, setSearchId] = useState<number | null>(initialEventId || null);

  const report = trpc.report.eventReport.useQuery(
    { eventId: searchId! },
    { enabled: searchId !== null && searchId > 0 }
  );

  const handleSearch = () => {
    const id = parseInt(eventId);
    if (isNaN(id) || id <= 0) {
      toast.error("Digite um ID de evento válido");
      return;
    }
    setSearchId(id);
  };

  const r = report.data;

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input
            placeholder="ID do evento (ex: 42)"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            className="pl-9 bg-white/[0.03] border-white/[0.08] text-white placeholder:text-white/25"
          />
        </div>
        <Button onClick={handleSearch} className="bg-purple-600 hover:bg-purple-700 text-white">
          <Search className="w-4 h-4 mr-1.5" /> Buscar
        </Button>
      </div>

      {report.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
          <span className="ml-2 text-white/40">Carregando relatório...</span>
        </div>
      )}

      {searchId && !report.isLoading && !r && (
        <div className="text-center py-12 text-white/30">
          <XCircle className="w-8 h-8 mx-auto mb-2" />
          Evento #{searchId} não encontrado
        </div>
      )}

      {r && (
        <div className="space-y-4">
          {/* Header */}
          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-400" />
                  Relatório do Evento #{r.id}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {r.isTrote && <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">TROTE</Badge>}
                  {r.isConflictEvent && <Badge className="bg-red-500/20 text-red-400 border-red-500/30">CONFLITO</Badge>}
                  <Badge className={`${STATUS_LABELS[r.status || ""]?.color || "text-white/50"} bg-white/[0.05] border-white/[0.08]`}>
                    {STATUS_LABELS[r.status || ""]?.label || r.status}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-white/30 block text-xs mb-1">Data/Hora</span>
                  <span className="text-white">{formatDate(r.createdAt)}</span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">Setor</span>
                  <span className="text-white">{r.sector}</span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">Tipo</span>
                  <span className="text-white">{EVENT_TYPE_LABELS[r.eventType || ""] || r.eventType}</span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">Classificação</span>
                  <span className={CLASSIFICATION_LABELS[r.classification || ""]?.color || "text-white/50"}>
                    {CLASSIFICATION_LABELS[r.classification || ""]?.label || r.classification}
                  </span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">Origem / Acionamento</span>
                  <span className="text-white text-xs">{r.origin}</span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">QR Code</span>
                  <span className="text-white">{r.sessionCode}</span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">Endereço</span>
                  <span className="text-white text-xs">{r.address || "N/A"}</span>
                </div>
                <div>
                  <span className="text-white/30 block text-xs mb-1">Status Final</span>
                  <span className={STATUS_LABELS[r.status || ""]?.color || "text-white/50"}>
                    {STATUS_LABELS[r.status || ""]?.label || r.status}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Victims & Aggressors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-amber-400 flex items-center gap-2">
                  <User className="w-4 h-4" /> Vítimas ({r.victims.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {r.victims.length === 0 ? (
                  <p className="text-white/25 text-xs">Nenhuma vítima registrada</p>
                ) : (
                  <div className="space-y-2">
                    {r.victims.map((v, i) => (
                      <div key={i} className="flex items-center justify-between bg-amber-500/5 rounded-md px-3 py-2 border border-amber-500/10">
                        <span className="text-white text-sm">{v.name}</span>
                        <span className="text-amber-400/60 text-xs">{v.confidence ? `${parseFloat(v.confidence).toFixed(0)}%` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Agressores ({r.aggressors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {r.aggressors.length === 0 ? (
                  <p className="text-white/25 text-xs">Nenhum agressor registrado</p>
                ) : (
                  <div className="space-y-2">
                    {r.aggressors.map((a, i) => (
                      <div key={i} className="flex items-center justify-between bg-red-500/5 rounded-md px-3 py-2 border border-red-500/10">
                        <span className="text-white text-sm">{a.name}</span>
                        <span className="text-red-400/60 text-xs">{a.confidence ? `${parseFloat(a.confidence).toFixed(0)}%` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Dispatches */}
          {r.dispatches.length > 0 && (
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-purple-400 flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Agentes Acionados ({r.dispatches.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {r.dispatches.map((d, i) => (
                    <div key={i} className="flex items-center justify-between bg-purple-500/5 rounded-md px-3 py-2 border border-purple-500/10">
                      <div>
                        <span className="text-white text-sm font-medium">{d.agentName}</span>
                        <span className="text-white/30 text-xs ml-2">({d.status})</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-white/40">
                        {d.dispatchedAt && <span>Acionado: {formatDate(d.dispatchedAt)}</span>}
                        {d.resolvedAt && <span className="text-green-400/60">Resolvido: {formatDate(d.resolvedAt)}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Evidence */}
          {r.images.length > 0 && (
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-cyan-400 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Evidências ({r.images.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {r.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/[0.03] border border-white/[0.06]">
                        <img src={img.imageUrl} alt={`Evidência ${i + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <div className="mt-1 text-[9px] text-white/30">
                        {img.origin} — {formatDate(img.createdAt)}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          {r.timeline.length > 0 && (
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white/60 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Timeline ({r.timeline.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {r.timeline.map((t, i) => (
                    <div key={i} className="flex items-start gap-3 py-1.5 border-b border-white/[0.03] last:border-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 shrink-0" />
                      <div className="flex-1">
                        <span className="text-white text-xs">{t.action}</span>
                        {t.description && <span className="text-white/30 text-xs ml-2">— {t.description}</span>}
                      </div>
                      <span className="text-white/20 text-[10px] shrink-0">{formatDate(t.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          {(r.notes || r.evidenceNotes) && (
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-white/60">Observações</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-white/50">
                {r.notes && <p>{r.notes}</p>}
                {r.evidenceNotes && <p className="mt-1 text-white/30">{r.evidenceNotes}</p>}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// V7.8: Sector Events Drill-down Panel
// ============================================================
function SectorEventsPanel({ sector, startDate, endDate, onBack, onSelectEvent }: {
  sector: string;
  startDate?: string;
  endDate?: string;
  onBack: () => void;
  onSelectEvent: (eventId: number) => void;
}) {
  const events = trpc.report.sectorEvents.useQuery({
    sector,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  return (
    <div className="space-y-4">
      {/* Back button + header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-purple-400 hover:text-purple-300 text-sm cursor-pointer">
          <ArrowLeft className="w-4 h-4" /> Voltar ao Ranking
        </button>
        <div className="h-4 w-px bg-white/10" />
        <h3 className="text-white font-semibold flex items-center gap-2">
          <MapPin className="w-4 h-4 text-purple-400" /> {sector}
        </h3>
      </div>

      {events.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      )}

      {events.data && events.data.length === 0 && (
        <div className="text-center py-12 text-white/25">
          <FileText className="w-8 h-8 mx-auto mb-2" />
          Nenhum evento neste setor
        </div>
      )}

      {events.data && events.data.length > 0 && (
        <>
          <div className="text-white/40 text-sm">{events.data.length} evento(s) encontrado(s)</div>

          {/* Table header */}
          <div className="grid grid-cols-[60px_1fr_120px_120px_100px_80px_80px] gap-2 px-3 py-2 text-[10px] text-white/25 uppercase tracking-wider border-b border-white/[0.06]">
            <span>ID</span>
            <span>Pessoa</span>
            <span>Classificação</span>
            <span>Status</span>
            <span>Agente</span>
            <span>Tipo</span>
            <span>Data</span>
          </div>

          {events.data.map((ev) => (
            <button
              key={ev.id}
              onClick={() => onSelectEvent(ev.id)}
              className="w-full grid grid-cols-[60px_1fr_120px_120px_100px_80px_80px] gap-2 px-3 py-2.5 text-xs rounded-md hover:bg-purple-500/5 border border-transparent hover:border-purple-500/15 cursor-pointer text-left transition-colors"
            >
              <span className="text-purple-400 font-medium">#{ev.id}</span>
              <span className="text-white truncate">{ev.personName}{ev.person2Name ? ` vs ${ev.person2Name}` : ""}</span>
              <span className={CLASSIFICATION_LABELS[ev.classification || ""]?.color || "text-white/40"}>
                {CLASSIFICATION_LABELS[ev.classification || ""]?.label || ev.classification}
              </span>
              <Badge className={`text-[10px] w-fit ${STATUS_LABELS[ev.status || ""]?.color || "text-white/40"} bg-white/[0.03] border-white/[0.06]`}>
                {STATUS_LABELS[ev.status || ""]?.label || ev.status}
              </Badge>
              <span className="text-white/40 truncate">{ev.agentName}</span>
              <span className="text-white/30 truncate">{EVENT_TYPE_LABELS[ev.eventType || ""] || ev.eventType}</span>
              <span className="text-white/25">{formatDateShort(ev.createdAt)}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}

// ============================================================
// TAB 2: Sector Analytics (with drill-down)
// ============================================================
function SectorAnalyticsTab({ onDrillToEvent }: { onDrillToEvent: (eventId: number) => void }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [drillSector, setDrillSector] = useState<string | null>(null);

  const analytics = trpc.report.sectorAnalytics.useQuery({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    sector: sectorFilter || undefined,
    eventType: typeFilter || undefined,
  });

  const data = analytics.data;

  // V7.8: If drill-down is active, show sector events panel
  if (drillSector) {
    return (
      <SectorEventsPanel
        sector={drillSector}
        startDate={startDate || undefined}
        endDate={endDate || undefined}
        onBack={() => setDrillSector(null)}
        onSelectEvent={onDrillToEvent}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="bg-white/[0.02] border-white/[0.06]">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Início</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-36 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Fim</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-36 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Setor</label>
              <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)}
                className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-white text-xs">
                <option value="">Todos</option>
                {data?.filters.uniqueSectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Tipo</label>
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-white text-xs">
                <option value="">Todos</option>
                {data?.filters.uniqueTypes.map(t => <option key={t} value={t}>{EVENT_TYPE_LABELS[t] || t}</option>)}
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={() => { setStartDate(""); setEndDate(""); setSectorFilter(""); setTypeFilter(""); }}
              className="text-white/40 border-white/[0.08] hover:bg-white/[0.03]">
              <XCircle className="w-3 h-3 mr-1" /> Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {analytics.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-white">{data.totalEvents}</div>
                <div className="text-[10px] text-white/30 uppercase tracking-wider">Total Eventos</div>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-purple-400">{data.sectors.length}</div>
                <div className="text-[10px] text-white/30 uppercase tracking-wider">Setores</div>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-red-400">{data.sectors.reduce((s, x) => s + x.conflicts, 0)}</div>
                <div className="text-[10px] text-white/30 uppercase tracking-wider">Conflitos</div>
              </CardContent>
            </Card>
            <Card className="bg-white/[0.02] border-white/[0.06]">
              <CardContent className="pt-4 text-center">
                <div className="text-2xl font-bold text-orange-400">{data.sectors.reduce((s, x) => s + x.trotes, 0)}</div>
                <div className="text-[10px] text-white/30 uppercase tracking-wider">Trotes</div>
              </CardContent>
            </Card>
          </div>

          {/* Ranking — V7.8: clickable rows for drill-down */}
          <Card className="bg-white/[0.02] border-white/[0.06]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-purple-400" /> Ranking de Setores
                  <span className="text-[10px] text-white/20 font-normal ml-2">Clique para ver eventos</span>
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => {
                  if (data.ranking.length > 0) {
                    downloadCSV(data.ranking, "ranking_setores");
                  }
                }} className="text-white/30 hover:text-white/60">
                  <Download className="w-3.5 h-3.5 mr-1" /> CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {data.ranking.length === 0 ? (
                <p className="text-white/25 text-xs text-center py-4">Nenhum dado disponível</p>
              ) : (
                <div className="space-y-1.5">
                  {data.ranking.map((r) => {
                    const maxTotal = data.ranking[0]?.total || 1;
                    const pct = (r.total / maxTotal) * 100;
                    return (
                      <button
                        key={r.sector}
                        onClick={() => setDrillSector(r.sector)}
                        className="w-full flex items-center gap-3 py-1 px-1 rounded-md hover:bg-purple-500/5 cursor-pointer transition-colors text-left"
                      >
                        <span className="text-white/30 text-xs w-6 text-right">#{r.rank}</span>
                        <span className="text-white text-sm w-32 truncate">{r.sector}</span>
                        <div className="flex-1 h-5 bg-white/[0.03] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              background: r.rank === 1 ? "linear-gradient(90deg, #7c3aed, #ec4899)" : "rgba(139,92,246,0.3)",
                            }}
                          />
                        </div>
                        <span className="text-white text-sm font-medium w-10 text-right">{r.total}</span>
                        {r.conflicts > 0 && <Badge className="bg-red-500/20 text-red-400 text-[10px] border-red-500/20">{r.conflicts} conf.</Badge>}
                        {r.trotes > 0 && <Badge className="bg-orange-500/20 text-orange-400 text-[10px] border-orange-500/20">{r.trotes} trote</Badge>}
                        <ChevronRight className="w-3.5 h-3.5 text-white/15" />
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sector detail cards — V7.8: clickable */}
          {data.sectors.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.sectors.slice(0, 6).map((s) => (
                <Card key={s.sector} className="bg-white/[0.02] border-white/[0.06] hover:border-purple-500/20 transition-colors cursor-pointer"
                  onClick={() => setDrillSector(s.sector)}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-white flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-purple-400" /> {s.sector}
                      <Badge className="ml-auto bg-purple-500/20 text-purple-400 text-[10px] border-purple-500/20">{s.total} eventos</Badge>
                      <ChevronRight className="w-3.5 h-3.5 text-white/15" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-white/30 block mb-1">Por Tipo</span>
                        {Object.entries(s.byType).map(([t, c]) => (
                          <div key={t} className="flex justify-between text-white/50">
                            <span>{EVENT_TYPE_LABELS[t] || t}</span>
                            <span className="text-white">{c}</span>
                          </div>
                        ))}
                      </div>
                      <div>
                        <span className="text-white/30 block mb-1">Por Status</span>
                        {Object.entries(s.byStatus).map(([st, c]) => (
                          <div key={st} className="flex justify-between text-white/50">
                            <span>{STATUS_LABELS[st]?.label || st}</span>
                            <span className="text-white">{c}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// TAB 3: Involved Persons (Rastreabilidade) — V7.8: clickable rows
// ============================================================
function InvolvedPersonsTab({ onSelectEvent }: { onSelectEvent: (eventId: number) => void }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [personName, setPersonName] = useState("");
  const [roleFilter, setRoleFilter] = useState<"victim" | "aggressor" | "all">("all");
  const [troteFilter, setTroteFilter] = useState<string>("");
  const [agentName, setAgentName] = useState("");
  const [qrCode, setQrCode] = useState("");

  const report = trpc.report.involvedPersons.useQuery({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    sector: sectorFilter || undefined,
    personName: personName || undefined,
    role: roleFilter === "all" ? undefined : roleFilter,
    isTrote: troteFilter === "" ? undefined : troteFilter === "true",
    agentName: agentName || undefined,
    qrCode: qrCode || undefined,
  });

  const exportData = trpc.report.exportData.useQuery({
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    sector: sectorFilter || undefined,
    personName: personName || undefined,
    isTrote: troteFilter === "" ? undefined : troteFilter === "true",
    agentName: agentName || undefined,
    qrCode: qrCode || undefined,
  }, { enabled: false });

  const handleExport = async () => {
    const result = await exportData.refetch();
    if (result.data) {
      downloadCSV(result.data, "relatorio_eventos");
    }
  };

  const data = report.data;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card className="bg-white/[0.02] border-white/[0.06]">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Início</label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-36 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Fim</label>
              <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                className="w-36 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Setor</label>
              <select value={sectorFilter} onChange={e => setSectorFilter(e.target.value)}
                className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-white text-xs">
                <option value="">Todos</option>
                {data?.filters.uniqueSectors.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Pessoa</label>
              <Input placeholder="Nome..." value={personName} onChange={e => setPersonName(e.target.value)}
                className="w-32 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Papel</label>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value as any)}
                className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-white text-xs">
                <option value="all">Todos</option>
                <option value="victim">Vítima</option>
                <option value="aggressor">Agressor</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Trote</label>
              <select value={troteFilter} onChange={e => setTroteFilter(e.target.value)}
                className="h-9 px-2 rounded-md bg-white/[0.03] border border-white/[0.08] text-white text-xs">
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">Agente</label>
              <Input placeholder="Nome..." value={agentName} onChange={e => setAgentName(e.target.value)}
                className="w-28 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-white/30 uppercase tracking-wider block mb-1">QR Code</label>
              <Input placeholder="Código..." value={qrCode} onChange={e => setQrCode(e.target.value)}
                className="w-28 bg-white/[0.03] border-white/[0.08] text-white text-xs" />
            </div>
            <Button variant="outline" size="sm" onClick={() => {
              setStartDate(""); setEndDate(""); setSectorFilter(""); setPersonName("");
              setRoleFilter("all"); setTroteFilter(""); setAgentName(""); setQrCode("");
            }} className="text-white/40 border-white/[0.08] hover:bg-white/[0.03]">
              <XCircle className="w-3 h-3 mr-1" /> Limpar
            </Button>
            <Button size="sm" onClick={handleExport} className="bg-purple-600 hover:bg-purple-700 text-white">
              <Download className="w-3.5 h-3.5 mr-1" /> Exportar CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {report.isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
        </div>
      )}

      {data && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-white/40 text-sm">{data.total} envolvido(s) encontrado(s)</span>
          </div>

          {data.persons.length === 0 ? (
            <div className="text-center py-12 text-white/25">
              <Users className="w-8 h-8 mx-auto mb-2" />
              Nenhum envolvido encontrado com os filtros aplicados
            </div>
          ) : (
            <div className="space-y-1.5">
              {/* Table header */}
              <div className="grid grid-cols-[60px_1fr_80px_1fr_60px_1fr_80px_80px] gap-2 px-3 py-2 text-[10px] text-white/25 uppercase tracking-wider border-b border-white/[0.06]">
                <span>Evento</span>
                <span>Pessoa</span>
                <span>Papel</span>
                <span>Ação</span>
                <span>Trote</span>
                <span>Setor</span>
                <span>Agente</span>
                <span>Data</span>
              </div>

              {data.persons.map((p, i) => (
                <button
                  key={`${p.eventId}-${p.personName}-${i}`}
                  onClick={() => onSelectEvent(p.eventId)}
                  className="w-full grid grid-cols-[60px_1fr_80px_1fr_60px_1fr_80px_80px] gap-2 px-3 py-2 text-xs rounded-md hover:bg-purple-500/5 border border-transparent hover:border-purple-500/15 cursor-pointer text-left transition-colors"
                >
                  <span className="text-purple-400">#{p.eventId}</span>
                  <span className="text-white truncate">{p.personName}</span>
                  <Badge className={`text-[10px] w-fit ${
                    p.role === "aggressor" ? "bg-red-500/20 text-red-400 border-red-500/20" :
                    p.role === "victim" ? "bg-amber-500/20 text-amber-400 border-amber-500/20" :
                    "bg-slate-500/20 text-slate-400 border-slate-500/20"
                  }`}>
                    {p.role === "aggressor" ? "Agressor" : p.role === "victim" ? "Vítima" : "N/A"}
                  </Badge>
                  <span className="text-white/40 truncate">{p.action}</span>
                  <span className={p.isTrote ? "text-orange-400" : "text-white/20"}>{p.isTrote ? "Sim" : "Não"}</span>
                  <span className="text-white/50 truncate">{p.sector}</span>
                  <span className="text-white/40 truncate">{p.agentName}</span>
                  <span className="text-white/30">{formatDateShort(p.eventDate)}</span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================================
// MAIN REPORTS PAGE — V7.8: drill-down state management
// ============================================================
export default function Reports() {
  const [activeTab, setActiveTab] = useState("event");
  const [drillEventId, setDrillEventId] = useState<number | null>(null);

  // V7.8: When drilling from sector/involved to event detail, switch to event tab
  const handleDrillToEvent = (eventId: number) => {
    setDrillEventId(eventId);
    setActiveTab("event");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #050510 0%, #0a0a1a 100%)" }}>
      <CommandNav pageTitle="Relatórios" />

      <div className="flex-1 p-4 md:p-6 max-w-7xl mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            <FileBarChart className="w-5 h-5 text-purple-400" />
            Relatórios Analíticos
          </h1>
          <p className="text-white/30 text-sm mt-1">Rastreabilidade completa de eventos, setores e envolvidos</p>
        </div>

        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setDrillEventId(null); }} className="w-full">
          <TabsList className="bg-white/[0.03] border border-white/[0.06] mb-4">
            <TabsTrigger value="event" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300 text-white/40">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Por Evento
            </TabsTrigger>
            <TabsTrigger value="sector" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300 text-white/40">
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" /> Por Setor
            </TabsTrigger>
            <TabsTrigger value="involved" className="data-[state=active]:bg-purple-500/20 data-[state=active]:text-purple-300 text-white/40">
              <Users className="w-3.5 h-3.5 mr-1.5" /> Envolvidos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="event">
            <EventReportTab key={drillEventId || "default"} initialEventId={drillEventId} />
          </TabsContent>
          <TabsContent value="sector">
            <SectorAnalyticsTab onDrillToEvent={handleDrillToEvent} />
          </TabsContent>
          <TabsContent value="involved">
            <InvolvedPersonsTab onSelectEvent={handleDrillToEvent} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
